import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { getCurrentUser, setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';

interface MFASetupProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  userEmail?: string;
}

const MFASetup: React.FC<MFASetupProps> = ({ open, onClose, onComplete, userEmail }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secretCode, setSecretCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [mfaSetupComplete, setMfaSetupComplete] = useState(false);

  const steps = [
    'Download Authenticator App',
    'Scan QR Code',
    'Verify Setup'
  ];

  useEffect(() => {
    if (open && activeStep === 1) {
      setupTOTPCode();
    }
  }, [open, activeStep]);

  const setupTOTPCode = async () => {
    try {
      setLoading(true);
      setError(null);

      // Set up TOTP
      const output = await setUpTOTP();
      const setupUri = output.getSetupUri('User Management System');
      
      // The secret code for manual entry
      setSecretCode(output.sharedSecret);
      
      // Convert URL object to string for QR code
      setQrCodeUrl(setupUri.toString());
      
    } catch (err: any) {
      console.error('Error setting up TOTP:', err);
      setError('Failed to set up MFA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyTOTP = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Verify the TOTP code
      await verifyTOTPSetup({ code: verificationCode });
      
      // Set TOTP as preferred MFA
      await updateMFAPreference({ totp: 'PREFERRED' });
      
      setMfaSetupComplete(true);
      setActiveStep(2);
      
    } catch (err: any) {
      console.error('Error verifying TOTP:', err);
      setError('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      verifyTOTP();
    } else if (activeStep === 2) {
      onComplete();
      handleClose();
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSecretCode('');
    setQrCodeUrl('');
    setVerificationCode('');
    setError(null);
    setMfaSetupComplete(false);
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Install an Authenticator App
            </Typography>
            <Typography paragraph>
              Before we can set up two-factor authentication, you'll need an authenticator app on your mobile device.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Recommended apps:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Google Authenticator (iOS/Android)</li>
              <li>Microsoft Authenticator (iOS/Android)</li>
              <li>Authy (iOS/Android)</li>
              <li>1Password (iOS/Android)</li>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Download and install one of these apps from your device's app store, then click "Next" to continue.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Scan QR Code
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography paragraph>
                  Open your authenticator app and scan the QR code below:
                </Typography>
                
                {qrCodeUrl && (
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center', mb: 2 }}>
                    <QRCodeSVG 
                      value={qrCodeUrl} 
                      size={200}
                      level="M"
                      includeMargin={true}
                    />
                  </Paper>
                )}
                
                <Typography variant="subtitle2" gutterBottom>
                  Can't scan the QR code?
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Manually enter this code in your authenticator app:
                </Typography>
                <Paper elevation={1} sx={{ p: 1, bgcolor: 'grey.100', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {secretCode}
                </Paper>
                
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Enter the 6-digit code from your authenticator app:
                  </Typography>
                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    placeholder="123456"
                    inputProps={{
                      maxLength: 6,
                      style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        );

      case 2:
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom color="success.main">
              🎉 MFA Setup Complete!
            </Typography>
            <Typography paragraph>
              Two-factor authentication has been successfully enabled for your account.
            </Typography>
            <Alert severity="success" sx={{ mt: 2 }}>
              Your admin account is now secured with multi-factor authentication. 
              You'll need to enter a code from your authenticator app each time you sign in.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Keep your authenticator app safe and accessible. If you lose access to it, 
              contact your administrator for assistance.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={activeStep === 1 && loading}
    >
      <DialogTitle>
        <Typography variant="h5">
          Set Up Multi-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Required for admin access
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        
        {activeStep > 0 && activeStep < 2 && (
          <Button 
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </Button>
        )}
        
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={loading || (activeStep === 1 && !verificationCode)}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : activeStep === steps.length - 1 ? (
            'Complete'
          ) : (
            'Next'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MFASetup;